import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { Image } from '@mantine/core';
import NavigationPanelButton from '@navigationPanel/components/NavigationPanelButton/NavigationPanelButton';
import useNavigationPanelIsAvailable from '@navigationPanel/hooks/useNavigationPanelIsAvailable';
import useNavigationPanelStore from '@navigationPanel/hooks/useNavigationPanelStore';
import HideOnMobile from 'src/components/HideOnMobile/HideOnMobile';
import { WHITE } from 'src/styles/colors';
import Logo from '../../../assets/img/icons/vb-logo.svg';
import CollapsableBreadcrumbs from '../../../components/CollapsableBreadcrumbs/CollapsableBreadcrumbs';
import BreadcrumbLevel from '../../../structures/breadcrumbLevel';

import './NavigationBreadcrumbs.scss';

export type NavigationBreadcrumbsProps = {
	breadcrumbs: BreadcrumbLevel[];
	linkURL?: string;
	hideHomeIcon?: boolean;
	hideFirstArrow?: boolean;
	homeIconClickable?: boolean;
	customHomeLogo?: string;
};

const HomeItem = ( { customHomeLogo }: { customHomeLogo?: string } ) => (
	customHomeLogo
		? <Image src={customHomeLogo} data-testid="custom-home-logo" alt="Home" width={82} height={20} fit="contain" ml="3px" mr="8px" />
		: <p>Home</p>
);

const WithOptionalLink = ( {
	children, to, className, enabled
}: { children: React.ReactNode, to: string, enabled: boolean, className?: string } ) => (
	enabled ? (
		<Link to={to} className={className}>{children}</Link>
	) : (
		<div className={className}>{children}</div>
	)
);

export const NavigationBreadcrumbs = ( {
	breadcrumbs,
	linkURL = '/',
	hideHomeIcon = false,
	hideFirstArrow = false,
	customHomeLogo = undefined,
	homeIconClickable = true
}: NavigationBreadcrumbsProps ) => {
	const navigationPanelStore = useNavigationPanelStore();
	const navigationPanelIsAvailable = useNavigationPanelIsAvailable();

	const showVeryBusyIcon = breadcrumbs.length === 0;
	const homeIcon = showVeryBusyIcon ? 'show-vb-logo' : 'show-home';

	// Add padding when sidebar is open to maintain alignment
	const shouldAddPadding = navigationPanelIsAvailable && navigationPanelStore.isOpen;

	return (
		<div className={classNames( 'NavigationBreadcrumbs', { 'p-with-padding': shouldAddPadding } )}>
			<HideOnMobile>
				<NavigationPanelButton
					action="open"
					color={WHITE}
				/>
			</HideOnMobile>
			{!hideHomeIcon && (
				<WithOptionalLink
					enabled={homeIconClickable}
					to={linkURL || '/'}
					className={classNames(
						homeIcon,
						{ 'hidden': navigationPanelIsAvailable && navigationPanelStore.isOpen }
					)}
				>
					{showVeryBusyIcon ? (
						<img data-testid="vb-logo" alt="VeryBusy logo" src={Logo} />
					) : (
						<HomeItem customHomeLogo={customHomeLogo} />
					)}
				</WithOptionalLink>
			)}
			{!showVeryBusyIcon && (
				<CollapsableBreadcrumbs breadcrumbs={breadcrumbs} hideFirstArrow={hideFirstArrow} />
			)}
		</div>
	);
};

export default observer( NavigationBreadcrumbs );
